import React, { PureComponent } from 'react';
import Select from '../Select/Select';
import Tree from '../Tree/Tree';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import createFakeEvent from '../../lib/createFakeEvent';
import { arrowDown, arrowUp, tab } from '../../lib/keyEvents';
import { isNil } from '../../lib/utils';

const recurseUpdateItem = (items, indices, updates) => {
  const itemsWithUpdates = items.slice();
  if (indices.length === 1) {
    itemsWithUpdates[indices[0]] = { ...itemsWithUpdates[indices[0]], ...updates };
    return itemsWithUpdates;
  } else {
    itemsWithUpdates[indices[0]].children = recurseUpdateItem(itemsWithUpdates[indices[0]].children, indices.slice(1), updates);
    return itemsWithUpdates;
  }
};

const recurseFindSelection = (items, value, valueKey = 'id') => {
  if (!value || typeof value !== 'object' || !items || items.length === 0) {
    return null;
  }
  const index = items.findIndex((item) => (
    `${item[valueKey]}` === `${value[valueKey]}`
  ));
  if (index > -1) {
    return `${index}`;
  } else {
    let found = null;
    items.forEach((item, idx) => {
      if (item.children) {
        const tmp = recurseFindSelection(item.children, value, valueKey);
        if (tmp) {
          found = `${idx}.${tmp}`;
        }
      }
    });
    return found;
  }
};

/**
 * `TreeSelect` is a select / dropdown component spesically for hierarchical data
 *
 *
 * @property {string} className The class name for this component. Will be appended to "core-tree-select".
 * @property {boolean} clearable If set to `true`, a **Clear** button will be shown on hover and focus. Defaults to `true`.
 * If you are embedding this component in a higher order component and want to control the visibility you can do that by
 * setting the *showClear* prop.
 * @property {function} hasMore A callback that should return `true` when there are more content to be loaded. *loadMore*
 * will only be called as long as *hasMore* returns `true`.
 * @property {string} itemDisabledKey attribute name to check if 'selectable' is true/false for items in case you want
 * items displayed without being selectable
 * @property {string} labelKey The key to used to find the display value if the autocomplete data provided is an array of
 * objects. Defaults to "label".
 * @property {boolean} lazyLoadChildren Determines if a "load more" button should be applied to children nodes.
 * the text set by *loadingText* instead. Defaults to "No items found".
 * @property {function} loadMore A callback that triggers loading of more content. *loadMore* will only be called as long as
 * your *loadMore* callback returns `true`.
 * @property {function} onBlur Callback for when component loses focus, beware that this is for some unknown reason also
 * triggered when users type a search term.
 * @property {function} onLoadChildren Callback for lazy loading children nodes
 * @property {string} onSearch Callback for when users search for an item
 * @property {function} onSelect Callback for events dispatched when an item in the dropdown is selected.
 * @property {boolean} readOnly Makes this input field read only. A read only input field will have a transparent background.
 * You may use *placeholder* as a default value that will be displayed when the actual value is undefined.
 * @property {boolean} showSearch If set to `true`, a search icon is displayed to the left of the input field. No search icon
 * is displayed by default.
 * @property {string|object} value The current value of the tree select dropdown
 * @property {string} valueKey The property name that defines the value for your objects in *options*. Defaults to "value".
 *
 * @see Input, Select, Tree
 */

const simulateClickOutside = () => {
  const modal = document.querySelector('div .input-select-list-modal');
  if (modal) {
    modal.click();
  }
};

export default class TreeSelect extends PureComponent {

  static displayName = 'TreeSelect';

  static recurseUpdateItem = recurseUpdateItem;

  static propTypes = {
    className: PropTypes.string,
    clearable: PropTypes.bool,
    hasMore: PropTypes.func,
    itemDisabledKey: PropTypes.string,
    itemRenderer: PropTypes.func,
    items: PropTypes.array,
    labelKey: PropTypes.string,
    lazyLoadChildren: PropTypes.bool,
    loadMore: PropTypes.func,
    onBlur: PropTypes.func,
    onLoadChildren: PropTypes.func,
    onSearch: PropTypes.func,
    onSelect: PropTypes.func,
    readOnly: PropTypes.bool,
    showSearch: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    valueKey: PropTypes.string,
  };

  static defaultProps = {
    clearable: true,
    items: [],
    lazyLoadChildren: true,
    readOnly: false,
    selection: {},
  };

  constructor(props) {
    super(props);

    const selection = this.findSelection(props);
    this.treeRef = null;
    this.selectionHandler = null;

    this.state = {
      selection,
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.items.length !== prevProps.items.length) {
      const selection = this.findSelection(this.props);
      if (JSON.stringify(selection) !== JSON.stringify(prevState.selection)) {
        this.setState({ selection });
      }
    }
  }

  findSelection(props) {
    let selection = {};

    if (props.value) {
      const selectionPath = recurseFindSelection(props.items, props.value, props.valueKey);
      if (selectionPath) {
        selection[selectionPath] = true;
      }
    }
    return selection;
  }

  onSearch = e => {
    const { onSelect, onSearch } = this.props;
    this.setState({ selection: {} });
    if (onSelect) {
      onSelect(createFakeEvent(null), null);
    }
    if (onSearch) {
      onSearch(e);
    }
  };

  onSelectionChanged = e => {
    this.setState({ selection: e.target.value });
  };

  onClear = e => {
    const { onClear } = this.props;
    if (onClear) {
      this.setState({ selection: {} });
      this.props.onSelect(createFakeEvent(null), null);
      onClear(e);
    }
  };

  getSelectedItemId = () => {
    const { selection } = this.state;
    const selectedIds = Object.keys(selection);
    if (selectedIds && selectedIds.length === 1) {
      const selectedId = selectedIds[0];
      if (selectedId.indexOf('.') === -1 && selection[selectedId]) {
        return selectedId;
      }
    }
    return null;
  };

  navigateToFirstItem = () => {
    if (this.treeRef) {
      const firstItem = this.treeRef.querySelector('.tree-node');
      if (firstItem) {
        firstItem.focus();
        const row = firstItem.getAttribute('data-row');
        const newSelection = this.selectionHandler.handleSelectionEvent(null, row, this.selection);
        this.onSelectionChanged(createFakeEvent(newSelection));
      }
    }
  };

  navigateToItem = itemId => {
    if (this.treeRef) {
      const item = this.treeRef.querySelector(`.tree-node#node-index-${itemId}`);
      if (item) {
        item.focus();
        const row = item.getAttribute('data-row');
        const newSelection = this.selectionHandler.handleSelectionEvent(null, row, this.selection);
        this.onSelectionChanged(createFakeEvent(newSelection));
        return true;
      }
      return false;
    }
  };

  onKeyDown = e => {
    if (arrowDown(e.key) || arrowUp(e.key)) {
      e.preventDefault();
      const selectedItemId = this.getSelectedItemId();
      if (isNil(selectedItemId)) {
        this.navigateToFirstItem();
      }
      const selectedItemIdValue = parseInt(selectedItemId, 10);
      if (arrowDown(e.key)) {
        const success = this.navigateToItem(selectedItemIdValue + 1);
        if (!success) {
          this.navigateToFirstItem()
        }
      } else {
        this.navigateToItem(selectedItemIdValue - 1);
      }
    }
    if (tab(e.key)) {
      simulateClickOutside();
    }
  };

  onSelect = (e, column, key) => {
    if (!key) {
      return;
    }
    this.props.onSelect(e, key);
  };

  setTreeRef = ref => {
    this.treeRef = ref;
  };

  setSelectionHandler = handler => {
    this.selectionHandler = handler;
  };

  render() {
    const {
      className,
      clearable,
      hasMore,
      itemDisabledKey,
      itemRenderer,
      items,
      labelKey,
      lazyLoadChildren,
      loading,
      loadMore,
      onBlur,
      onExpandCollapse,
      onLoadChildren,
      onSearch,
      onSelect,
      pageStart,
      readOnly,
      selection,
      showSearch,
      value,
      valueKey,
      ...rest
    } = this.props;

    const classes = cx('core-tree-select', className);

    return (
      <Select
        {...rest}
        data-key="id"
        className={classes}
        clearable={clearable}
        data-preventmenuclose
        dropdown={<Tree
          data-preventmenuclose
          getSelectionHandler={this.setSelectionHandler}
          hasMore={hasMore}
          innerRef={this.setTreeRef}
          itemDisabledKey={itemDisabledKey}
          itemRenderer={itemRenderer}
          items={items}
          lazyLoadChildren={lazyLoadChildren}
          loading={loading}
          loadMore={loadMore}
          onClick={this.onSelect}
          onExpandCollapse={onExpandCollapse}
          onLoadChildren={onLoadChildren}
          onSelectionChanged={this.onSelectionChanged}
          pageStart={pageStart}
          selection={this.state.selection}
        />}
        iconName="search"
        labelKey={labelKey}
        onBlur={onBlur}
        onChange={this.onSearch}
        onClear={this.onClear}
        onKeyDown={this.onKeyDown}
        readOnly={readOnly}
        style={{ width: '100%' }}
        value={value}
        valueKey={valueKey}
      />
    )
  }

}
